import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { ReasonCode } from "@app/enum/ReasonCode";
import { ILocationInformation } from "@app/interfaces/ILocationInformation";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IPulseRecordPayload } from "@app/main/modules/foundation/_routes/hardware/_models/IPulseRecordPayload";
import { ValidationCheckType } from "@app/main/modules/foundation/_services/device-data-validation.service";
import { DriverTachoMode, IDriverTachoHours } from "@app/services/tacho.service";
import { TranslationService } from "@app/services/translation.service";
import { IAampAlarmData, IAampCompiledTestObject, IAampGpsData, IDeviceDayCalendar, IDeviceToAdd, IftCloudAlarmData, IftCloudCompiledTestObject, IFtCloudFootageHistory, IFtCloudGpsData, IValidationResults } from "@foundation/foundation.interfaces";
import { IPlot } from "@interfaces/IPlot";
import { ISensorData } from "@interfaces/ISensorData";
import { IDeviceInfo, IS3VideoFile } from "@services/cameraCommunicationServer.service";
import { GeocodeService } from "@services/geocode.service";
import { ITemperatureRecord } from "@temperature/_models/temperature/ITemperatureRecord";
import angular from "angular";
import "./validation-check-container.component.scss";

@Component({
    selector: "validation-check-container",
    template: require('./validation-check-container.component.html')
})

export class ValidationCheckContainerComponent implements OnChanges {
    @Input() deviceToAdd: IDeviceToAdd;
    @Input() newDevice: boolean;
    @Input() includeTemperatureTests: boolean;
    @Input() commissionVehicle: IVehicle;

    @Output() onValidationComplete: EventEmitter<IValidationResults> = new EventEmitter();
    @Output() deviceToAddChanged: EventEmitter<IDeviceToAdd> = new EventEmitter();

    // ---

    showCheckDetails = true;

    plots: IPlot[];
    sensorData: ISensorData[];
    videos: IS3VideoFile[];
    aampTestData: IAampCompiledTestObject;
    aampGpsPackets: IAampGpsData[];
    aampAlarmPackets: IAampAlarmData[];
    ftCloudTestData: IftCloudCompiledTestObject;
    temperatureRecords: ITemperatureRecord[];
    digiDlData: IDriverTachoHours[];
    pulsePacketString: string;

    is4gCamera: boolean;
    isMdvr: boolean;
    isTranscan: boolean;

    unitLocation: string;

    // ---- eums ----

    DriverTachoMode = DriverTachoMode;

    // ---

    constructor(
        private readonly geocodeService: GeocodeService,
        private readonly vTableService: VTableService,
        private readonly translationService: TranslationService) {
    }

    ngOnInit(): void {
        this.createDataTables();
    }

    ngOnChanges() {
        this.sensorData = null;
        this.plots = null;
        this.aampTestData = null
    }

    toggleCheckDetails() {

        this.showCheckDetails = !this.showCheckDetails;
    }

    onFaultRecordChecked(event: any): void {
        this.deviceToAdd.faultRecorded = event;
    }


    validationComplete(results: IValidationResults): void {
        this.assignData(results);

        this.createTableRows();

        this.onValidationComplete.emit(results);
    }

    get noData(): boolean {
        return !this.plots
            && !this.temperatureRecords
            && !this.sensorData
            && !this.aampTestData
            && !this.ftCloudTestData
            && !this.digiDlData
            && !this.pulsePacketString?.length;
    }

    // ---

    private createTableRows(): void {
        this.resetTables();

        this.gpsTableRows = this.vTableService.createTableRows(this.plots, this.gpsTableColumns);

        this.temperatureTableRows = this.vTableService.createTableRows(this.temperatureRecords, this.temperatureTableColumns);

        this.sensorFuelDataTableRows = this.vTableService.createTableRows(this.sensorData, this.sensorFuelDataTableColumns);
        this.sensorTimingsDataTableRows = this.vTableService.createTableRows(this.sensorData, this.sensorTimingsDataTableColumns);
        this.sensorDriverDataTableRows = this.vTableService.createTableRows(this.sensorData, this.sensorDriverDataTableColumns);

        this.aampGpsDataTableRows = this.vTableService.createTableRows(this.aampTestData?.GpsRecords, this.aampGpsDataTableColumns);
        this.aampAlarmDataTableRows = this.vTableService.createTableRows(this.aampTestData?.AlarmRecords, this.aampAlarmDataTableColumns);
        this.aampFootageHistoryDataTableRows = this.vTableService.createTableRows(this.aampTestData?.FootageRecords, this.aampFootageHistoryDataTableColumns);

        this.ftCloudGpsDataTableRows = this.vTableService.createTableRows(this.ftCloudTestData?.GpsRecords, this.ftCloudGpsDataTableColumns);
        this.ftCloudAlarmDataTableRows = this.vTableService.createTableRows(this.ftCloudTestData?.AlarmRecords, this.ftCloudAlarmDataTableColumns);
        this.ftCloudFootageHistoryDataTableRows = this.vTableService.createTableRows(this.ftCloudTestData?.FootageRecords, this.ftCloudFootageHistoryDataTableColumns);

        this.digiDataTableRows = this.vTableService.createTableRows(this.digiDlData, this.digiDataTableColumns);
    }

    private resetTables(): void {
        this.gpsTableRows = [];
        this.temperatureTableRows = [];
        this.sensorFuelDataTableRows = [];
        this.sensorTimingsDataTableRows = [];
        this.sensorDriverDataTableRows = [];
        this.aampGpsDataTableRows = [];
        this.aampAlarmDataTableRows = [];
        this.aampFootageHistoryDataTableRows = [];
        this.ftCloudGpsDataTableRows = [];
        this.ftCloudAlarmDataTableRows = [];
        this.ftCloudFootageHistoryDataTableRows = [];
        this.digiDataTableRows = [];        
    }

    private assignData(results: IValidationResults): void {
        this.plots = results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.Plot)?.TestData as IPlot[];

        if (this.plots?.length) {
            this.plots.forEach(p => p.ReasonText = this.translationService.tEnum(ReasonCode, 'ReasonCode', p.Reason));
            this.resolveAddress(this.plots[this.plots.length - 1].Latitude, this.plots[this.plots.length - 1].Longitude);
        }

        this.pulsePacketString = null;
        this.videos = [];
        if (!!results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.AddsecureCamera)?.TestData) {
            const rtlP100Data = results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.AddsecureCamera)?.TestData[0] as IDeviceInfo;

            if (!!rtlP100Data.Lat && !!rtlP100Data.Lon) {
                this.resolveAddress(rtlP100Data.Lat, rtlP100Data.Lon);
                this.videos = rtlP100Data.CameraLatestVideos;
            } else {
                const veriP100Data = results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.AddsecureCamera)?.TestData[0] as IPulseRecordPayload;
                this.pulsePacketString = JSON.stringify(veriP100Data, null, 2);
                this.resolveAddress(veriP100Data.GPS?.latitude, veriP100Data.GPS?.longitude);
            }
        }

        this.sensorData = results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.SensorData)?.TestData as ISensorData[];
        this.temperatureRecords = results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.Temperature)?.TestData as ITemperatureRecord[];
        this.digiDlData = results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.DigiDl)?.TestData as IDriverTachoHours[];

        if (!!results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.FtCloud)?.TestData) {
            this.ftCloudTestData = results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.FtCloud)?.TestData[0] as IftCloudCompiledTestObject;
        }

        if (!!results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.Ceiba)?.TestData) {
            this.aampTestData = results.checksRun.find(c => c.ValidationCheckType == ValidationCheckType.Ceiba)?.TestData[0] as IAampCompiledTestObject;
        }

        if (this.ftCloudTestData?.GpsRecords?.length) {
            this.resolveAddress(this.ftCloudTestData?.GpsRecords[0].Latitude, this.ftCloudTestData?.GpsRecords[0].Longitude);
        }

        if (this.aampTestData?.GpsRecords?.length) {
            this.resolveAddress(this.aampTestData.GpsRecords[0].GpsLat, this.aampTestData.GpsRecords[0].GpsLng);
        }
    }

    private resolveAddress(latitutde: number, longitude: number): Promise<void> {
        if (latitutde === undefined && longitude === undefined) {

            this.unitLocation = this.translationService.t('Common.Unknown');
            return Promise.resolve()
        }

        return new Promise<void>((resolve, reject) => {
            this.geocodeService.getAddressFromPoint(new google.maps.LatLng(latitutde, longitude))
                .then((location: ILocationInformation) => {
                    if (location?.LocationDescription) {
                        this.unitLocation = location.LocationDescription;
                    } else {
                        this.unitLocation = this.translationService.t('Common.Unknown');
                    }
                    resolve();
                })
                .catch((error: any) => {
                    console.error("Error in resolveAddress:", error);
                    reject(error);
                });
        });
    }

    // --------- Data tables setup ------------------

    private createDataTables(): void {
        this.createGpsTables();
        this.createTemperatureTable();
        this.createSensorFuelDataTable();
        this.createSensorTimingsDataTable();
        this.createSensorDriverDataTable();
        this.createAampGpsDataTable();
        this.createAampAlarmDataTable();
        this.createAampFootageHistoryDataTable();
        this.createFtCloudGpsDataTable();
        this.createFtCloudAlarmDataTable();
        this.createFtCloudFootageHistoryDataTable();
        this.createDigiDataTable();
    }

    gpsTableColumns: IVTableColumn<IPlot>[];
    gpsTableRows: IVTableRow[];

    private createGpsTables(): void {
        this.gpsTableColumns = [
            {
                header: this.vTableService.createDateTableHeader(),
                createCellFunction: (plot) => this.vTableService.createDateCell(new Date(plot.Utc))
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.ReasonCode') }),
                createCellFunction: (plot) => this.vTableService.createTableCell(plot.ReasonText)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('Common.Odometer') }),
                createCellFunction: (plot) => this.vTableService.createTableCell(plot.Odometer)
            }
        ];
    }

    temperatureTableColumns: IVTableColumn<ITemperatureRecord>[];
    temperatureTableRows: IVTableRow[];

    private createTemperatureTable(): void {
        this.temperatureTableColumns = [
            {
                header: this.vTableService.createDateTableHeader(),
                createCellFunction: (temp) => this.vTableService.createDateCell(new Date(temp.Timestamp))
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.PlotId') }),
                createCellFunction: (temp) => this.vTableService.createTableCell(temp.PlotId)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Probe') }),
                createCellFunction: (temp) => this.vTableService.createTableCell(temp.TemperatureProbeId)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('Common.Temperature') }),
                createCellFunction: (temp) => this.vTableService.createTableCell(temp.Temperature)
            }
        ];
    }

    sensorFuelDataTableColumns: IVTableColumn<ISensorData>[];
    sensorFuelDataTableRows: IVTableRow[];

    private createSensorFuelDataTable(): void {
        this.sensorFuelDataTableColumns = [
            {
                header: this.vTableService.createDateTableHeader(),
                createCellFunction: (sensorData) => this.vTableService.createDateCell(new Date(sensorData.Utc))
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Header') }),
                createCellFunction: (sensorData) => this.vTableService.createTableCell(sensorData.Header)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.PtoFuel') }),
                createCellFunction: (sensorData) => this.vTableService.createTableCell(sensorData.FuelUsedPto)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.IdleFuel') }),
                createCellFunction: (sensorData) => this.vTableService.createTableCell(sensorData.FuelUsedIdle)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.TotalFuel') }),
                createCellFunction: (sensorData) => this.vTableService.createTableCell(sensorData.FuelUsedTotal)
            }
        ];
    }

    sensorTimingsDataTableColumns: IVTableColumn<ISensorData>[];
    sensorTimingsDataTableRows: IVTableRow[];

    private createSensorTimingsDataTable(): void {
        this.sensorTimingsDataTableColumns = [
            {
                header: this.vTableService.createDateTableHeader(),
                createCellFunction: (sensorData) => this.vTableService.createDateCell(new Date(sensorData.Utc))
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.PtoTime') }),
                createCellFunction: (sensorData) => this.vTableService.createTableCell(sensorData.PtoDuration)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.IdleTime') }),
                createCellFunction: (sensorData) => this.vTableService.createTableCell(sensorData.IdlingDuration)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.EngineOnTime') }),
                createCellFunction: (sensorData) => this.vTableService.createTableCell(sensorData.EngineOnDuration)
            }
        ];
    }

    sensorDriverDataTableColumns: IVTableColumn<ISensorData>[];
    sensorDriverDataTableRows: IVTableRow[];

    private createSensorDriverDataTable(): void {
        this.sensorDriverDataTableColumns = [
            {
                header: this.vTableService.createDateTableHeader(),
                createCellFunction: (sensorData) => this.vTableService.createDateCell(new Date(sensorData.Utc))
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('Common.Tachograph') }),
                createCellFunction: (sensorData) => this.vTableService.createTableCell(sensorData.TachoSlot1Driver)
            }
        ];
    }

    aampGpsDataTableColumns: IVTableColumn<IAampGpsData>[];
    aampGpsDataTableRows: IVTableRow[];

    private createAampGpsDataTable(): void {
        this.aampGpsDataTableColumns = [
            {
                header: this.vTableService.createDateTableHeader(),
                createCellFunction: (aampGpsData) => this.vTableService.createDateCell(new Date(aampGpsData.GpsTime))
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Latitude') }),
                createCellFunction: (aampGpsData) => this.vTableService.createTableCell(aampGpsData.GpsLat)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Longitude') }),
                createCellFunction: (aampGpsData) => this.vTableService.createTableCell(aampGpsData.GpsLng)
            }
        ];
    }

    aampAlarmDataTableColumns: IVTableColumn<IAampAlarmData>[];
    aampAlarmDataTableRows: IVTableRow[];

    private createAampAlarmDataTable(): void {
        this.aampAlarmDataTableColumns = [
            {
                header: this.vTableService.createDateTableHeader(),
                createCellFunction: (aampAlarmData) => this.vTableService.createDateCell(new Date(aampAlarmData.GpsTime))
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Latitude') }),
                createCellFunction: (aampAlarmData) => this.vTableService.createTableCell(aampAlarmData.GpsLat)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Longitude') }),
                createCellFunction: (aampAlarmData) => this.vTableService.createTableCell(aampAlarmData.GpsLng)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.AlarmType') }),
                createCellFunction: (aampAlarmData) => this.vTableService.createTableCell(aampAlarmData.Content)
            }
        ];
    }

    aampFootageHistoryDataTableColumns: IVTableColumn<IDeviceDayCalendar>[];
    aampFootageHistoryDataTableRows: IVTableRow[];

    private createAampFootageHistoryDataTable(): void {
        this.aampFootageHistoryDataTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Channel') }),
                createCellFunction: (aampFootageHistoryData) => this.vTableService.createTableCell(aampFootageHistoryData.chn)
            },
            {
                header: this.vTableService.createDateTableHeader({ title: this.translationService.t('ValidationCheckContainer.FootageStart') }),
                createCellFunction: (aampFootageHistoryData) => this.vTableService.createDateCell(new Date(aampFootageHistoryData.starttime))
            },
            {
                header: this.vTableService.createDateTableHeader({ title: this.translationService.t('ValidationCheckContainer.FootageEnd') }),
                createCellFunction: (aampFootageHistoryData) => this.vTableService.createDateCell(new Date(aampFootageHistoryData.endtime))
            }
        ];
    }

    ftCloudGpsDataTableColumns: IVTableColumn<IFtCloudGpsData>[];
    ftCloudGpsDataTableRows: IVTableRow[];

    private createFtCloudGpsDataTable(): void {
        this.ftCloudGpsDataTableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ title: this.translationService.t('Common.Time') }),
                createCellFunction: (ftCloudGpsData) => this.vTableService.createDateCell(new Date(ftCloudGpsData.EventTime))
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Latitude') }),
                createCellFunction: (ftCloudGpsData) => this.vTableService.createTableCell(ftCloudGpsData.Latitude)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Longitude') }),
                createCellFunction: (ftCloudGpsData) => this.vTableService.createTableCell(ftCloudGpsData.Longitude)
            }
        ];
    }

    ftCloudAlarmDataTableColumns: IVTableColumn<IftCloudAlarmData>[];
    ftCloudAlarmDataTableRows: IVTableRow[];

    private createFtCloudAlarmDataTable(): void {
        this.ftCloudAlarmDataTableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ title: this.translationService.t('Common.Time') }),
                createCellFunction: (ftCloudAlarmData) => this.vTableService.createDateCell(new Date(ftCloudAlarmData.StartTime))
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Latitude') }),
                createCellFunction: (ftCloudAlarmData) => this.vTableService.createTableCell(ftCloudAlarmData.gpsInfo.Latitude)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Longitude') }),
                createCellFunction: (ftCloudAlarmData) => this.vTableService.createTableCell(ftCloudAlarmData.gpsInfo.Longitude)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.AlarmType') }),
                createCellFunction: (ftCloudAlarmData) => this.vTableService.createTableCell(ftCloudAlarmData.AlarmType)
            }
        ];
    }

    ftCloudFootageHistoryDataTableColumns: IVTableColumn<IFtCloudFootageHistory>[];
    ftCloudFootageHistoryDataTableRows: IVTableRow[];

    private createFtCloudFootageHistoryDataTable(): void {
        this.ftCloudFootageHistoryDataTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Channel') }),
                createCellFunction: (ftCloudFootageHistoryData) => this.vTableService.createTableCell(ftCloudFootageHistoryData.Channel)
            },
            {
                header: this.vTableService.createDateTableHeader({ title: this.translationService.t('ValidationCheckContainer.FootageStart'), sorted: true }),
                createCellFunction: (ftCloudFootageHistoryData) => this.vTableService.createDateCell(ftCloudFootageHistoryData.StartTime)
            },
            {
                header: this.vTableService.createDateTableHeader({ title: this.translationService.t('ValidationCheckContainer.FootageEnd') }),
                createCellFunction: (ftCloudFootageHistoryData) => this.vTableService.createDateCell(new Date(ftCloudFootageHistoryData.EndTime))
            }
        ];
    }

    digiDataTableColumns: IVTableColumn<IDriverTachoHours>[];
    digiDataTableRows: IVTableRow[];

    private createDigiDataTable(): void {
        this.digiDataTableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ title: this.translationService.t('Common.Time') }),
                createCellFunction: (digiDlData) => this.vTableService.createDateCell(new Date(digiDlData.EventTime))
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.Mode') }),
                createCellFunction: (digiDlData) => this.vTableService.createEnumCell(DriverTachoMode, "DriverTachoMode", digiDlData.Mode)
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('ValidationCheckContainer.RemainingDrive') }),
                createCellFunction: (digiDlData) => this.vTableService.createTableCell(digiDlData.RemainingDrive)
            }
        ];
    }
}
